import { Injectable, Injector, effect, inject, signal, untracked } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { PRIMARY_OUTLET, RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { DivisionBrandingService } from '../services/division-branding.service';

/**
 * WorkplaceTitleStrategy — composes the page title from
 *   <route title> — RICOH Workplace [· <Division>]
 *
 * Route configs declare bare names (`title: 'Map'`); this strategy adds
 * the product suffix and, when the user is logged in to a division (or
 * customer) with a name set, appends that division as a context anchor.
 *
 * Re-applies the title whenever DivisionBrandingService.branding()
 * changes — the leaf division is computed from QuickLinks data which
 * loads after boot, so the very first render usually picks up the
 * default and the title needs to refresh once branding settles.
 *
 * AI CONTEXT: this class is built BY the Router. `NavigationTransitions`
 * injects `TitleStrategy` as an instance field, so every service reachable
 * from here is constructed inside the Router's own constructor — and any one
 * of them that injects `Router` closes a cycle and kills app boot with
 * NG0200. That is not hypothetical: `QuickLinksService` (two hops away,
 * through DivisionBrandingService) took a Router dependency and the whole app
 * went white. Reach the branding graph LAZILY, from inside the effect, where
 * the Router already exists — then a service is free to inject `Router`,
 * which is an ordinary thing for a service to want. Guarded by
 * `workplace-title.strategy.spec.ts`.
 */
const PRODUCT_NAME = 'RICOH Workplace';
const DEFAULT_TITLE = PRODUCT_NAME;

@Injectable({ providedIn: 'root' })
export class WorkplaceTitleStrategy extends TitleStrategy {
  private readonly titleService = inject(Title);
  private readonly injector = inject(Injector);
  private brandingService?: DivisionBrandingService;

  /** Last route-derived page title — re-applied when branding changes. */
  private readonly pageTitle = signal<string>('');
  /** Games has its own identity; ordinary workplace routes keep the suffix. */
  private readonly standaloneTitle = signal(false);

  constructor() {
    super();
    effect(() => {
      const division = this.divisionSuffix();
      const page = this.pageTitle();
      this.titleService.setTitle(
        this.standaloneTitle() && page ? page : this.compose(page, division),
      );
    });
  }

  override updateTitle(snapshot: RouterStateSnapshot): void {
    const routeTitle = this.buildTitle(snapshot) ?? '';
    let route = snapshot.root;
    let standalone = false;
    while (route) {
      if (route.data['standaloneTitle'] === true) standalone = true;
      const child = route.children.find(child => child.outlet === PRIMARY_OUTLET);
      if (!child) break;
      route = child;
    }
    this.standaloneTitle.set(standalone);
    this.pageTitle.set(routeTitle);
  }

  /** Returns the active division/customer name, or null when only the
   *  default product fallback is in play (we don't suffix in that case). */
  private divisionSuffix(): string | null {
    // The construction is untracked, the branding() read is not: resolving a
    // service pulls in its collaborators' constructors, and any signal those
    // happen to read would otherwise become a dependency of this effect.
    this.brandingService ??= untracked(() =>
      this.injector.get(DivisionBrandingService),
    );
    const name = this.brandingService.branding().name;
    if (!name || name === PRODUCT_NAME || name === 'RICOH Workplace') return null;
    return name;
  }

  private compose(page: string, division: string | null): string {
    if (!page) {
      return division ? `${PRODUCT_NAME} · ${division}` : DEFAULT_TITLE;
    }
    if (division) return `${page} — ${PRODUCT_NAME} · ${division}`;
    return `${page} — ${PRODUCT_NAME}`;
  }
}
